import type { Vector3 } from 'three';

import type { CameraManager, SceneTransitionManager } from '../../managers';
import type { SceneObject } from '../../types';

export type AnimateIslandObjectsByTypeParams = {
	objects: Array<SceneObject>;
	islandData?: { center: { x: number; y: number; z: number }; objects: Array<SceneObject> };
	isInitialRender: boolean;
	sceneTransitionManager: SceneTransitionManager | null;
	cameraManager: CameraManager | null;
	getCurrentPresentationPoint: () => Vector3 | null;
	emit: (type: string, data: unknown) => void;
};

/**
 * Animate island objects in sequence by type order.
 * Parallel groups are animated together, sequential types one at a time.
 */
export const animateIslandObjectsByTypeCfn = async (params: AnimateIslandObjectsByTypeParams): Promise<void> => {
	const {
		objects, islandData, isInitialRender, sceneTransitionManager,
		cameraManager, getCurrentPresentationPoint, emit
	} = params;

	if (!sceneTransitionManager || objects.length === 0) {
		return;
	}

	const islandCenter = islandData?.center;
	const sequence = sceneTransitionManager.getIslandSequence();
	const { typeOrder, parallelGroups } = sequence;

	// Group objects by type
	const byType = new Map<string, Array<SceneObject>>();
	for (const obj of objects) {
		const type = obj.metadata.type ?? 'unknown';
		if (!byType.has(type)) {
			byType.set(type, []);
		}
		byType.get(type)!.push(obj);
	}

	// Helper to animate a batch of objects
	const animateBatch = async (batchObjects: Array<SceneObject>): Promise<void> => {
		if (batchObjects.length === 0) return;

		const camPos = cameraManager?.camera?.position;
		const cameraPosition = camPos ? { x: camPos.x, y: camPos.y, z: camPos.z } : undefined;
		const presentationPoint = getCurrentPresentationPoint();

		const promises = batchObjects.map((obj) => {
			const isAppliance = obj.metadata.type === 'appliance';
			const useSceneCenter = isAppliance && presentationPoint !== null;
			const animationCenter = useSceneCenter
				? { x: presentationPoint!.x, y: presentationPoint!.y, z: presentationPoint!.z }
				: islandCenter;
			return sceneTransitionManager.animateAdditionByType(obj, animationCenter, isInitialRender, useSceneCenter, cameraPosition);
		});
		await Promise.all(promises);

		for (const obj of batchObjects) {
			emit('object:added', { id: obj.metadata.id, type: obj.metadata.type });
		}
	};

	// Process in type order
	const processedTypes = new Set<string>();

	for (const type of typeOrder) {
		if (processedTypes.has(type)) continue;

		// Check if this type is part of a parallel group
		const parallelGroup = parallelGroups?.find((g) => g.includes(type as any));

		if (parallelGroup) {
			// Animate all types in the parallel group together
			const parallelObjects: Array<SceneObject> = [];
			for (const groupType of parallelGroup) {
				const typeObjects = byType.get(groupType) || [];
				parallelObjects.push(...typeObjects);
				processedTypes.add(groupType);
			}
			await animateBatch(parallelObjects);
		} else {
			const typeObjects = byType.get(type) || [];
			await animateBatch(typeObjects);
			processedTypes.add(type);
		}
	}

	// Handle any types not in the order
	for (const [type, typeObjects] of byType) {
		if (!processedTypes.has(type) && typeObjects.length > 0) {
			await animateBatch(typeObjects);
		}
	}
};
